import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface Notification {
  id: string;
  alertType: string;
  message: string;
  triggeredAt: string;
  acknowledged: boolean;
  severity: string;
  metadata?: any;
}

interface NotificationsContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  fetchNotifications: () => Promise<void>;
  acknowledgeNotification: (alertId: string) => Promise<void>;
  acknowledgeAll: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

interface NotificationsProviderProps {
  children: ReactNode;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get token from localStorage
  const getToken = () => localStorage.getItem('heat_portal_token');

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    const token = getToken();
    console.log('[NotificationsContext] fetchNotifications called, hasToken:', !!token);
    
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/portal/alerts?acknowledged=false&limit=50', {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[NotificationsContext] API Response:', data);
      console.log('[NotificationsContext] Has success field:', data.success);
      console.log('[NotificationsContext] Has data field:', data.data);
      
      if (data.success) {
        const alerts = data.data || [];
        console.log('[NotificationsContext] Setting notifications:', alerts.length, 'alerts');
        setNotifications(alerts);
        setUnreadCount(alerts.length);
      } else {
        console.warn('[NotificationsContext] API response.success is false or missing');
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Acknowledge a single notification
  const acknowledgeNotification = useCallback(async (alertId: string) => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`/api/portal/alerts/${alertId}/read`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== alertId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error acknowledging notification:', err);
      throw err;
    }
  }, []);

  // Acknowledge all notifications
  const acknowledgeAll = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    if (notifications.length === 0) return;

    try {
      const response = await fetch('/api/portal/alerts/read-all', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Update local state
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Error acknowledging all notifications:', err);
      throw err;
    }
  }, [notifications.length]);

  // Fetch notifications on mount and set up polling
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    fetchNotifications();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);

    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const value: NotificationsContextType = {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    acknowledgeNotification,
    acknowledgeAll
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};
